import ExcelJS from "exceljs";
// src/utils/guestSpendingCsvUtils.js

/**
 * Convert an array of objects into a CSV string.
 * Uses the object keys of the first element as headers.
 * @param {Object[]} objArray
 * @returns {string}
 */
export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let str = val == null ? "" : String(val);
    if (str.includes('"')) {
      str = str.replace(/"/g, '""');
    }
    return `"${str}"`;
  };

  // header row
  let csv = headers.map(escape).join(",") + "\r\n";

  // data rows
  objArray.forEach((row) => {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  });

  return csv;
}

// export function exportGuestsAndSpendingCSV({
//   chartData,
//   selectedYear,
//   range,
//   storess,
//   fileName = "GuestsAndSpending.csv",
// }) {
//   if (!chartData || !chartData.labels || !chartData.datasets) {
//     console.error("exportGuestsAndSpendingCSV: invalid chartData");
//     return;
//   }

//   // Extract arrays from datasets
//   const guestDataset = chartData.datasets.find(
//     (ds) => ds.label === "Guest Count",
//   );
//   const avgSpendDataset = chartData.datasets.find(
//     (ds) => ds.label === "Avg Spend per Guest",
//   );
//   if (!guestDataset || !avgSpendDataset) {
//     console.error("exportGuestsAndSpendingCSV: missing required datasets");
//     return;
//   }
//   const totalGuests = guestDataset.data;
//   const avgSpendPerGuest = avgSpendDataset.data;

//   // Parse range
//   const dateRange = typeof range === "string" ? JSON.parse(range) : range || {};

//   // Build row objects
//   const rows = chartData.labels.map((label, i) => ({
//     Brand: "Angels Pizza",
//     Store: storess || "All Stores",
//     Year: `${selectedYear}${
//       dateRange.$lte?.split("T")[0] || ""
//     } ${selectedYear}${dateRange.$gte?.split("T")[0] || ""}`,
//     TotalGuests: totalGuests[i],
//     AvgSpendPerGuest: avgSpendPerGuest[i],
//   }));

//   // Convert to CSV
//   const csvString = convertToCSV(rows);
//   if (!csvString) {
//     console.warn("exportGuestsAndSpendingCSV: no data to export");
//     return;
//   }

//   // Trigger download
//   const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }

export async function exportGuestsAndSpendingCSV({
  chartData,
  selectedYear,
  range,
  storess,
  fileName = "GuestsAndSpending.xlsx",
}) {
  if (!chartData?.labels?.length) {
    console.error("exportGuestsAndSpendingXLSX: invalid chartData.labels");
    return;
  }

  const guests = (chartData.monthlyCounts || []).map((v) => Number(v ?? 0));
  const avgSpend = (chartData.monthlyAverages || []).map((v) => Number(v ?? 0));

  if (!guests.length || !avgSpend.length) {
    console.error(
      "exportGuestsAndSpendingXLSX: missing monthlyCounts/monthlyAverages",
    );
    return;
  }

  // Parse date range safely
  const parsedRange =
    typeof range === "string" ? JSON.parse(range) : range || {};
  const from = parsedRange?.$gte ? new Date(parsedRange.$gte) : null;
  const to = parsedRange?.$lte ? new Date(parsedRange.$lte) : null;

  const dateRangeLabel =
    from && to
      ? `${from.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })} - ${to.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}`
      : `${selectedYear || ""}`;

  // Build rows
  const rows = chartData.labels.map((label, i) => ({
    Brand: "Angels Pizza",
    Store: storess || "All Stores",
    Period: label,
    TotalGuests: guests[i] ?? 0,
    AvgSpendPerGuest: avgSpend[i] ?? 0,
  }));

  const totalGuests = guests.reduce((a, b) => a + b, 0);
  const avgSpendOverall =
    avgSpend.length > 0
      ? avgSpend.reduce((a, b) => a + b, 0) / avgSpend.length
      : 0;

  // ===== Workbook =====
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Analytics Export";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Guests vs Spend", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  // ===== Title =====
  sheet.mergeCells("A1:E1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "Total Guest vs. Avg Spend per Guest";
  titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F2937" },
  };
  sheet.getRow(1).height = 26;

  // ===== Meta =====
  sheet.mergeCells("A2:E2");
  const metaCell = sheet.getCell("A2");
  metaCell.value = `Brand: Angels Pizza   |   Store: ${
    storess || "All Stores"
  }   |   Date: ${dateRangeLabel}`;
  metaCell.font = { italic: true, size: 11, color: { argb: "FF111827" } };
  metaCell.alignment = { vertical: "middle", horizontal: "left" };
  metaCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };
  sheet.getRow(2).height = 18;

  // Spacer
  sheet.addRow([]);
  sheet.getRow(3).height = 6;

  // ===== Header =====
  const headers = [
    "Brand",
    "Store",
    "Period",
    "Total Guests",
    "Avg Spend per Guest",
  ];
  sheet.addRow(headers);

  // Data rows
  rows.forEach((r) => {
    sheet.addRow([
      r.Brand,
      r.Store,
      r.Period,
      r.TotalGuests,
      r.AvgSpendPerGuest,
    ]);
  });

  // Column widths
  sheet.columns = [
    { width: 16 },
    { width: 18 },
    { width: 18 },
    { width: 16 },
    { width: 22 },
  ];

  const headerRowIndex = 4;
  const firstDataRow = headerRowIndex + 1;
  const lastDataRow = headerRowIndex + rows.length;

  // Style header row
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 18;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2563EB" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Body rows
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    const row = sheet.getRow(r);
    const isAlt = (r - firstDataRow) % 2 === 1;

    row.eachCell((cell, col) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      if (col <= 3) {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      } else {
        cell.alignment = { vertical: "middle", horizontal: "center" };
      }

      if (col === 4) cell.numFmt = "#,##0";
      if (col === 5) cell.numFmt = '"₱"#,##0.00';

      if (isAlt) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
      }
    });
  }

  // ===== Summary Row =====
  const summaryRowIndex = lastDataRow + 1;
  sheet.mergeCells(`A${summaryRowIndex}:C${summaryRowIndex}`);

  const summaryLabel = sheet.getCell(`A${summaryRowIndex}`);
  summaryLabel.value = "SUMMARY";
  summaryLabel.font = { bold: true };
  summaryLabel.alignment = { vertical: "middle", horizontal: "left" };
  summaryLabel.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  sheet.getCell(`D${summaryRowIndex}`).value = totalGuests;
  sheet.getCell(`D${summaryRowIndex}`).numFmt = "#,##0";
  sheet.getCell(`D${summaryRowIndex}`).font = { bold: true };

  sheet.getCell(`E${summaryRowIndex}`).value = avgSpendOverall;
  sheet.getCell(`E${summaryRowIndex}`).numFmt = '"₱"#,##0.00';
  sheet.getCell(`E${summaryRowIndex}`).font = { bold: true };

  // Auto-filter
  sheet.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: headers.length },
  };

  // ===== Download =====
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(link.href), 500);
}
